import {ReactNode, useRef, useState} from "react";
import {Box, Popover} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import BorderVerticalIcon from "@mui/icons-material/BorderVertical";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import CloseIcon from "@mui/icons-material/Close";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import MergeIcon from "@mui/icons-material/CallMerge";
import RoofingIcon from "@mui/icons-material/Roofing";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import SpaceBarIcon from "@mui/icons-material/SpaceBar";
import SquareIcon from "@mui/icons-material/Square";
import HeightIcon from "@mui/icons-material/Height";
import {ElevationApp} from "../../ElevationApp";
import {MdSelection} from "../../model/view/MdViewSettings";
import {MdFrameJoint} from "../../model/frame/MdFrameBlock";
import {frameProfileLabels, MdFrameProfileType} from "../../model/frame/MdFrameSpec";
import {supportedProfiles} from "../../scenes/frameProfile/Helpers";
import {useEventEmitter2} from "../../util/useEventEmitter2";
import {RibbonButton, RibbonGroup, RibbonSelect, RibbonSmallButton, RibbonStack, RibbonVSplitButton} from "../Ribbon";
import {RibbonAngle} from "../RibbonAngle";
import {JOINT_OPTIONS} from "../JointOptions";
import {canFlipSlope, canMakeAirGap, canMakeAngled, canMakeSloped, canRemoveStick, canStartJoin, canStartSplitBy, distributeSticks, findStickById, flipSlope, flipCross, getCornerJoint, getStickProfile, getStickProfileFaces, insertBesideStick, makeAirGap, makeAngled, makeMullion, makeSloped, pieceStickId, removeAngle, removeSlope, setCornerJoint, setStickAngle, setStickProfile, setStickThickness, stickCorners, StickInsertSide} from "../../scenes/frameGrid/BlockOps";
import {stickThicknessVar} from "../../scenes/frameGrid/Util";
import {MenubarShortcut, useMenubarShortcuts} from "../useMenubarShortcuts";
import {emitChanged, emitRebuild, remove, startJoin, startSplitBy} from "./helpers";
import {StickAnchorsGroup} from "./StickAnchorsGroup";

type StickSel = Extract<MdSelection, {type: "stick"}>;

// "inherit" clears a face's override so it follows the neighbouring cell / spec default.
const INHERIT = "inherit";
const PROFILE_OPTIONS = [
    {value: INHERIT, label: "Default (Spec)"},
    ...supportedProfiles.map(p => ({value: p as string, label: frameProfileLabels[p]})),
];

export function StickSelection(props: {app: ElevationApp; selection: StickSel[]}) {
    const app = props.app;
    const sel = props.selection;
    const multi = sel.length > 1;
    // the single-stick joint/insert groups act on one stick; when several are
    // selected they're hidden, so those computations use the first stick only.
    const stick = sel[0];
    // the model stick the selected piece draws (a piece split off beside a step has
    // its own selection id). The model edits below act on it; corners act on the piece.
    const stickId = pieceStickId(stick.id);

    // re-render when a joint edit fires frameBlocksChanged so the active states
    // below reflect the new config (the edit doesn't re-emit the selection).
    useEventEmitter2(app.frameSpec.frameBlocksChanged);

    const insert = (side: StickInsertSide) => {
        if(multi) return;
        if(insertBesideStick(app.frameSpec, stickId, side)) {
            emitChanged(app);
        } else {
            app.draw.showError("Can't insert here");
        }
    };

    // a stick on the frame outline may meet a perpendicular stick at each end (a corner).
    const corners = stickCorners(app.frameSpec, stick.id);

    // a perimeter piece can split where another stick meets it, so each part can step
    // on its own.
    const canSplitBy = !multi && canStartSplitBy(app.frameSpec, stickId);

    // the opposite: fold a stick that was split from this one back into it.
    const canJoin = !multi && canStartJoin(app.frameSpec, stickId);

    const model = findStickById(app.frameSpec, stickId);

    // an interior stick may be an air gap: open space between its two cells instead of
    // stock. The stock-only groups (joints, profiles, slope, angle, anchors) go while it
    // is one; the gap's size is edited in their place. The tab title names the kind, so
    // the selection is re-sent after a change.
    const airGap = !!model?.airGap;
    const canGap = !multi && (airGap || canMakeAirGap(app.frameSpec, stickId));
    const toggleAirGap = () => {
        if(airGap ? makeMullion(app.frameSpec, stickId) : makeAirGap(app.frameSpec, stickId)) {
            emitChanged(app);
            app.viewSettings.selection.emit([...sel]);
        } else {
            app.draw.showError("Can't make an air gap here");
        }
    };
    const [gapAnchor, setGapAnchor] = useState<HTMLElement | null>(null);
    const gapRef = useRef<HTMLDivElement | null>(null);
    const gapSize = model ? stickThicknessVar(model, app.frameSpec).get() : 0;
    const setGap = (value: number) => {
        if(setStickThickness(app.frameSpec, stickId, value)) emitChanged(app);
    };

    // an outline stick may slope: the cells under it are shorter at one end.
    const sloped = !!model?.slope;
    const canSlope = !multi && (sloped || canMakeSloped(app.frameSpec, stickId));
    const toggleSloped = () => {
        if(sloped ? removeSlope(app.frameSpec, stickId) : makeSloped(app.frameSpec, stickId)) emitChanged(app);
        else app.draw.showError("Can't slope this stick");
    };
    // the slope stays put while a stick along the edge needs the tall end where it is.
    const canFlip = sloped && canFlipSlope(app.frameSpec, stickId);
    const flipSloped = () => {
        if(flipSlope(app.frameSpec, stickId)) emitChanged(app);
    };

    // a double-sided floor-to-ceiling mullion may turn in plan: the elevation stays
    // flat, the top-down section shows the angle.
    const angle = model?.angle;
    const canAngle = !multi && canMakeAngled(app.frameSpec, stickId);
    const addAngle = () => {
        if(makeAngled(app.frameSpec, stickId)) emitChanged(app);
        else app.draw.showError("Can't angle this stick");
    };
    const setAngle = (deg: number) => {
        if(setStickAngle(app.frameSpec, stickId, deg)) emitChanged(app);
    };
    const dropAngle = () => {
        if(removeAngle(app.frameSpec, stickId)) emitChanged(app);
    };

    // interior joint info (computed from layout). At a cross this stick is either
    // the through (extended) stick — `crossLines` — or a capping one, whose A / B
    // end meets a cross it can extend into (`extendA` / `extendB` name the through
    // stick to flip). Otherwise it may be a plain tee (forced cap, no flip).
    const joint = app.frameSpec.jointInfo.get(stickId);
    const crossLines = joint?.crossLines ?? [];
    const isThrough = crossLines.length > 0;
    const extendA = isThrough ? undefined : joint?.extendA;
    const extendB = isThrough ? undefined : joint?.extendB;
    const isCrossCap = !!extendA || !!extendB;
    const isTee = !!joint?.isTee;
    const vertical = stick.orientation === "vertical";

    // a corner change only re-terminates existing sticks, so reconcile in place.
    // locked corners (on the floor) reject the edit — only reconcile on success.
    const setJoint = (key: string, type: MdFrameJoint) => {
        if(setCornerJoint(app.frameSpec, key, type)) emitChanged(app);
    };

    // flip a cross by transposing the through stick's parent block (a rebuild).
    // `throughId` is this stick when it runs through, or the partner across the
    // chosen end when this stick is a capping one. The acted-on stick (stickId) is
    // kept through the flip and re-selected, so its (now flipped) ribbon stays up.
    const flipThrough = (throughId: string) => {
        const lines = app.frameSpec.jointInfo.get(throughId)?.crossLines ?? [];
        if(flipCross(app.frameSpec, throughId, lines, stickId)) {
            emitRebuild(app);
            app.viewSettings.selection.emit([{type: "stick", id: stickId, orientation: stick.orientation}]);
            app.viewSettings.activeTab.emit("selected");
        } else {
            app.draw.showError("Can't flip this joint");
        }
    };

    // the profile faces this stick exposes (Left/Right for an interior mullion, a single
    // "Profile" for a vertical perimeter jamb, none otherwise). Single-stick only.
    const profileFaces = multi ? [] : getStickProfileFaces(app.frameSpec, stickId);

    // override (or inherit, when "inherit") the frame profile on one face of the stick.
    const setProfile = (face: "left" | "right", value: string) => {
        const profile = value === INHERIT ? undefined : (value as MdFrameProfileType);
        if(setStickProfile(app.frameSpec, stickId, face, profile)) emitChanged(app);
    };

    // equalize the cells bounded by the selected sticks (per contiguous run within
    // a row/column). Enabled only for a multi-selection.
    const distribute = () => {
        if(!multi) return;
        if(distributeSticks(app.frameSpec, sel.map(s => pieceStickId(s.id)))) emitChanged(app);
    };

    // arrow keys insert beside this mullion, mirroring the Insert buttons' arrow
    // icons: a vertical mullion splits a row (left/right), a horizontal one splits
    // a column (above/below). Each side's descriptor drives both its key and button.
    const insertHint = stick.orientation === "vertical"
        ? "Press ← or → to insert a mullion"
        : "Press ↑ or ↓ to insert a mullion";
    const sides: (MenubarShortcut & {icon: ReactNode; label: string})[] = stick.orientation === "vertical"
        ? [
            {icon: <ArrowBackIcon />, label: "Left", keys: ["ArrowLeft"], display: "←", hint: insertHint, run: () => insert("left")},
            {icon: <ArrowForwardIcon />, label: "Right", keys: ["ArrowRight"], display: "→", hint: insertHint, run: () => insert("right")},
        ]
        : [
            {icon: <ArrowUpwardIcon />, label: "Above", keys: ["ArrowUp"], display: "↑", hint: insertHint, run: () => insert("above")},
            {icon: <ArrowDownwardIcon />, label: "Below", keys: ["ArrowDown"], display: "↓", hint: insertHint, run: () => insert("below")},
        ];

    // Delete removes the selected stick(s), but only removable ones (see
    // canRemoveStick) — so the shortcut and its Actions button appear only when at
    // least one selected stick is removable.
    const removableIds = Array.from(new Set(sel.map(s => pieceStickId(s.id)))).filter(id => canRemoveStick(app.frameSpec, id));
    const canRemove = removableIds.length > 0;
    const removeShortcut: MenubarShortcut = {
        keys: ["Delete", "Backspace"], display: "Delete",
        hint: multi ? "Press Delete to remove the mullions" : "Press Delete to remove the mullion",
        run: () => removableIds.forEach(id => remove(app, id)),
    };
    // S / J run Split By / Join, only while that button is enabled.
    const splitByShortcut: MenubarShortcut = {
        keys: ["s"], display: "S", hint: "Press S to split by another stick",
        run: () => startSplitBy(app, stickId),
    };
    const joinShortcut: MenubarShortcut = {
        keys: ["j"], display: "J", hint: "Press J to join another stick",
        run: () => startJoin(app, stickId),
    };

    // insert hotkeys are single-stick only; drop them during a multi-selection.
    useMenubarShortcuts([
        ...(multi ? [] : sides),
        ...(canRemove ? [removeShortcut] : []),
        ...(canSplitBy ? [splitByShortcut] : []),
        ...(canJoin ? [joinShortcut] : []),
    ]);

    const insertGroup = vertical ? (
        <RibbonGroup label="Insert">
            {sides.map(s => (
                <RibbonButton key={s.display} icon={s.icon} label={s.label} shortcut={s.display} disabled={multi} onClick={s.run} />
            ))}
        </RibbonGroup>
    ) : (
        <RibbonGroup label="Mullion">
            <RibbonVSplitButton
                label="Insert"
                disabled={multi}
                top={{icon: sides[0].icon, label: "Insert Above", shortcut: sides[0].display, onClick: sides[0].run}}
                bottom={{icon: sides[1].icon, label: "Insert Below", shortcut: sides[1].display, onClick: sides[1].run}}
            />
        </RibbonGroup>
    );

    // a perimeter border or a door-bordering mullion can't be removed, so omit the
    // Actions group entirely when nothing selected is removable
    const actionsGroup = canRemove && (
        <RibbonGroup label="Actions">
            <RibbonButton
                icon={<DeleteOutlineIcon sx={{color: "error.main"}} />}
                label="Remove"
                shortcut={removeShortcut.display}
                onClick={removeShortcut.run}
            />
        </RibbonGroup>
    );

    const arrangeGroup = (
        <RibbonGroup label="Arrange">
            <RibbonButton
                icon={<ViewWeekIcon />}
                label="Distribute"
                disabled={!multi}
                onClick={distribute}
            />
        </RibbonGroup>
    );

    if(airGap) {
        return (
            <>
                {insertGroup}

                <RibbonGroup label="Air Gap">
                    <Box ref={gapRef} sx={{display: "flex", height: "100%"}}>
                        <RibbonButton
                            icon={<HeightIcon sx={{transform: vertical ? "rotate(90deg)" : undefined}} />}
                            label={app.draw.formatDimension(gapSize)}
                            onClick={() => setGapAnchor(gapRef.current)}
                        />
                    </Box>
                    <RibbonButton icon={<SquareIcon />} label="Make Mullion" onClick={toggleAirGap} />
                </RibbonGroup>

                {actionsGroup}
                {arrangeGroup}

                <Popover
                    open={!!gapAnchor}
                    anchorEl={gapAnchor}
                    onClose={() => setGapAnchor(null)}
                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                >
                    {app.draw.renderDimension({
                        value: gapSize,
                        locked: true,
                        onLockChange: () => {},
                        onDone: (value: number) => {
                            setGapAnchor(null);
                            setGap(value);
                        },
                        onCancel: () => setGapAnchor(null),
                    })}
                </Popover>
            </>
        );
    }

    return (
        <>
            {insertGroup}

            <RibbonGroup label="Outline">
                <RibbonStack>
                    <RibbonSmallButton
                        icon={<ContentCutIcon />}
                        label="Split By"
                        shortcut={splitByShortcut.display}
                        disabled={!canSplitBy}
                        onClick={splitByShortcut.run}
                    />
                    <RibbonSmallButton
                        icon={<MergeIcon />}
                        label="Join"
                        shortcut={joinShortcut.display}
                        disabled={!canJoin}
                        onClick={joinShortcut.run}
                    />
                </RibbonStack>
                <RibbonStack>
                    <RibbonSmallButton
                        icon={sloped ? <HorizontalRuleIcon /> : <RoofingIcon />}
                        label={sloped ? "Make Straight" : "Make Sloped"}
                        disabled={!canSlope}
                        onClick={toggleSloped}
                    />
                    {sloped && (
                        <RibbonSmallButton
                            icon={vertical ? <SwapVertIcon /> : <SwapHorizIcon />}
                            label="Flip Slope"
                            disabled={!canFlip}
                            onClick={flipSloped}
                        />
                    )}
                </RibbonStack>
            </RibbonGroup>

            <RibbonGroup label="Angle">
                {angle && !multi ? (
                    <RibbonAngle value={angle.get()} onChange={setAngle} onRemove={dropAngle} />
                ) : (
                    <RibbonButton
                        icon={<SquareFootIcon />}
                        label="Make Angled"
                        disabled={!canAngle}
                        onClick={addAngle}
                    />
                )}
            </RibbonGroup>

            <RibbonGroup label="Air Gap">
                <RibbonButton
                    icon={<SpaceBarIcon />}
                    label="Make Air Gap"
                    disabled={!canGap}
                    onClick={toggleAirGap}
                />
            </RibbonGroup>

            {/* single-stick joint editors: one group per corner this stick touches
                (pick how the two sticks meeting there are terminated — miter, or one
                extends / the other caps; miter is hidden at threshold corners). */}
            {!multi && corners.map(c => (
                <RibbonGroup key={c.key} label={`${c.label} Corner`}>
                    <RibbonStack>
                        {JOINT_OPTIONS.filter(o => o.type !== "miter" || c.allowMiter).map(o => (
                            <RibbonSmallButton
                                key={o.type}
                                icon={o.icon}
                                label={o.label}
                                active={getCornerJoint(app.frameSpec, c.key) === o.type}
                                disabled={c.locked}
                                onClick={() => setJoint(c.key, o.type)}
                            />
                        ))}
                    </RibbonStack>
                </RibbonGroup>
            ))}

            {/* this stick is part of a cross. The through stick is extended both ways
                (those buttons active, read-only) and can be Capped to flip. A capping
                stick can Extend the end (Top/Bottom or Left/Right) that meets a cross
                — the other end is disabled. Either way the flip transposes the block. */}
            {!multi && (isThrough || isCrossCap) && (
                <RibbonGroup label="Cross">
                    <RibbonButton
                        icon={vertical ? <ArrowUpwardIcon /> : <ArrowBackIcon />}
                        label={vertical ? "Extend Top" : "Extend Left"}
                        active={isThrough}
                        disabled={isThrough || !extendA}
                        onClick={() => extendA && flipThrough(extendA)}
                    />
                    <RibbonButton
                        icon={vertical ? <ArrowDownwardIcon /> : <ArrowForwardIcon />}
                        label={vertical ? "Extend Bottom" : "Extend Right"}
                        active={isThrough}
                        disabled={isThrough || !extendB}
                        onClick={() => extendB && flipThrough(extendB)}
                    />
                    <RibbonButton
                        icon={<BorderVerticalIcon />}
                        label="Split"
                        active={!isThrough}
                        disabled={!isThrough}
                        onClick={() => flipThrough(stickId)}
                    />
                </RibbonGroup>
            )}

            {/* a plain tee: this stick caps into a perpendicular run mid-span with no
                aligned partner to cross, so the cap is forced — shown read-only. */}
            {!multi && !isThrough && !isCrossCap && isTee && (
                <RibbonGroup label="Joint">
                    <RibbonButton icon={<CloseIcon />} label="Capped" active disabled onClick={() => {}} />
                </RibbonGroup>
            )}

            {actionsGroup}

            {/* per-face frame-profile override: one picker for a vertical perimeter jamb
                ("Profile"), two for an interior mullion ("Left" / "Right"). */}
            {profileFaces.map(f => (
                <RibbonGroup key={f.face} label={f.label}>
                    <RibbonSelect
                        value={getStickProfile(app.frameSpec, stickId, f.face) ?? INHERIT}
                        options={PROFILE_OPTIONS}
                        onChange={value => setProfile(f.face, value)}
                    />
                </RibbonGroup>
            ))}

            {!multi && <StickAnchorsGroup app={app} pieceId={stick.id} />}

            {arrangeGroup}
        </>
    );
}
